/**
 * Jaulas en préstamo: catálogo de jaulas (foto, color, tamaño) + préstamos a
 * clientes (con fecha de devolución y alerta de vencimiento). Funciona en modo
 * API (Postgres/Oracle) y Firebase.
 */
import {
  collection,
  doc,
  query,
  orderBy,
  where,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';

import { firestore } from './config';
import { USE_API, api, apiQuery } from '@/services/api/client';
import type { Timestamp } from 'firebase/firestore';

export interface Jaula {
  id: string;
  nombre: string;
  color?: string | null;
  tamano?: string | null;
  descripcion?: string | null;
  fotoUrl?: string | null;
  estado: 'disponible' | 'prestada';
  clienteNombre?: string | null;
  clienteCelular?: string | null;
  garantiaMonto?: number | null;
  prestadaDesde?: Timestamp | null;
  prestadaHasta?: Timestamp | null;
  prestadaEn?: Timestamp | null;
  prestadaPorNombre?: string | null;
  orden?: number;
}

export interface PrestamoJaula {
  id: string;
  jaulaId: string;
  jaulaNombre: string;
  clienteNombre: string;
  clienteCelular?: string | null;
  garantiaMonto?: number | null;
  desde?: Timestamp | null;
  hasta?: Timestamp | null;
  prestadaEn: Timestamp;
  prestadaPorNombre?: string | null;
  devueltaEn?: Timestamp | null;
}

export const jaulasQuery: any = USE_API
  ? apiQuery('/jaulas', { _order: 'orden' })
  : query(collection(firestore, 'jaulas'), orderBy('orden'));

export const prestamosJaulaQuery: any = USE_API
  ? apiQuery('/prestamos_jaula', { _order: '-prestada_en' })
  : query(collection(firestore, 'prestamos_jaula'), orderBy('prestadaEn', 'desc'));

export interface UpsertJaulaInput {
  id?: string;
  nombre: string;
  color?: string | null;
  tamano?: string | null;
  descripcion?: string | null;
  fotoUrl?: string | null;
}

export async function upsertJaula(input: UpsertJaulaInput): Promise<string> {
  if (USE_API) {
    const body = { nombre: input.nombre, color: input.color ?? null, tamano: input.tamano ?? null, descripcion: input.descripcion ?? null, fotoUrl: input.fotoUrl ?? null };
    if (input.id) return (await api.patch(`/jaulas/${input.id}`, body)).id ?? input.id;
    return (await api.post('/jaulas', { ...body, orden: 100 })).id;
  }
  const id = input.id ?? doc(collection(firestore, 'jaulas')).id;
  await setDoc(
    doc(collection(firestore, 'jaulas'), id),
    {
      nombre: input.nombre,
      color: input.color ?? null,
      tamano: input.tamano ?? null,
      descripcion: input.descripcion ?? null,
      fotoUrl: input.fotoUrl ?? null,
      updatedAt: serverTimestamp(),
      ...(input.id ? {} : { estado: 'disponible', orden: 100, createdAt: serverTimestamp() }),
    } as any,
    { merge: true },
  );
  return id;
}

export interface PrestarJaulaInput {
  jaulaId: string;
  clienteNombre: string;
  clienteCelular?: string | null;
  garantiaMonto?: number | null;
  desde?: string | null; // YYYY-MM-DD
  hasta: string; // YYYY-MM-DD
  prestadaPorNombre?: string | null;
}

export async function prestarJaula(input: PrestarJaulaInput): Promise<void> {
  if (USE_API) {
    await api.post('/acciones/prestar-jaula', input);
    return;
  }
  // Firebase: actualiza la jaula (sin transacción de historial completa).
  await updateDoc(doc(collection(firestore, 'jaulas'), input.jaulaId), {
    estado: 'prestada',
    clienteNombre: input.clienteNombre,
    clienteCelular: input.clienteCelular ?? null,
    garantiaMonto: input.garantiaMonto ?? null,
    prestadaHasta: input.hasta,
    prestadaEn: serverTimestamp(),
    prestadaPorNombre: input.prestadaPorNombre ?? null,
    updatedAt: serverTimestamp(),
  } as any);
}

export async function eliminarJaula(jaulaId: string): Promise<void> {
  if (USE_API) {
    await api.delete(`/jaulas/${jaulaId}`);
    return;
  }
  await deleteDoc(doc(collection(firestore, 'jaulas'), jaulaId));
}

export async function devolverJaula(jaulaId: string): Promise<void> {
  if (USE_API) {
    await api.post('/acciones/devolver-jaula', { jaulaId });
    return;
  }
  await updateDoc(doc(collection(firestore, 'jaulas'), jaulaId), {
    estado: 'disponible',
    clienteNombre: null,
    clienteCelular: null,
    prestadaHasta: null,
    prestadaEn: null,
    prestadaPorNombre: null,
    updatedAt: serverTimestamp(),
  } as any);
}
